import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import GalleryGrid from "@/components/GalleryGrid";
import CommentSection from "@/components/CommentSection";
import ShareButtons from "@/components/ShareButtons";
import { useTranslateContent } from "@/hooks/useTranslateContent";
import { Heart, HeartOff, ChevronLeft, Zap, Swords, X, ChevronRight, ChevronLeft as ChevLeft, ImageIcon, Trophy, Film, Users, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";

// Lightbox component for enlarging images
const Lightbox = ({ images, currentIndex, onClose, onNext, onPrev }) => {
  if (currentIndex < 0) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
      onClick={onClose}
      data-testid="lightbox-overlay"
    >
      <button onClick={onClose} className="absolute top-4 right-4 text-white/70 hover:text-white z-10" data-testid="lightbox-close">
        <X className="w-8 h-8" />
      </button>
      {images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white z-10"
            data-testid="lightbox-prev"
          >
            <ChevLeft className="w-5 h-5" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white z-10"
            data-testid="lightbox-next"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </>
      )}
      <img
        src={images[currentIndex]}
        alt=""
        className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg"
        onClick={(e) => e.stopPropagation()}
        data-testid="lightbox-image"
      />
      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
          {images.map((_, i) => (
            <div key={i} className={`w-2 h-2 rounded-full transition-all ${i === currentIndex ? "bg-white" : "bg-white/30"}`} />
          ))}
        </div>
      )}
    </div>
  );
};

const CharacterPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [character, setCharacter] = useState(null);
  const [universe, setUniverse] = useState(null);
  const [weapons, setWeapons] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [comments, setComments] = useState([]);
  const [isFavorite, setIsFavorite] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  const [heroIdx, setHeroIdx] = useState(0);
  const [heroPaused, setHeroPaused] = useState(false);

  const allImages = character ? [character.image, ...(character.images || [])].filter(Boolean) : [];
  const uniqueImages = [...new Set(allImages)];

  // Auto-rotate hero image every 4 seconds
  useEffect(() => {
    if (uniqueImages.length <= 1 || heroPaused) return;
    const timer = setInterval(() => {
      setHeroIdx((prev) => (prev + 1) % uniqueImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [uniqueImages.length, heroPaused]);

  const fetchData = useCallback(async () => {
    try {
      const charRes = await axios.get(`${API}/characters/${id}`);
      setCharacter(charRes.data);
      
      axios.post(`${API}/view/character/${id}`).catch(() => {});

      const universeRes = await axios.get(`${API}/universes`);
      const uni = universeRes.data.find(u => u.id === charRes.data.universe_id);
      setUniverse(uni);

      const [galleryRes, commentsRes] = await Promise.all([
        axios.get(`${API}/gallery?character_id=${id}`),
        axios.get(`${API}/comments?target_type=character&target_id=${id}`),
      ]);
      setGallery(galleryRes.data);
      setComments(commentsRes.data);

      // Fetch weapons for this character's universe
      if (charRes.data.universe_id) {
        try {
          const wpnRes = await axios.get(`${API}/weapons?universe_id=${charRes.data.universe_id}`);
          setWeapons(wpnRes.data || []);
        } catch { /* ignore */ }
      }

      if (user) {
        try {
          const favRes = await axios.get(`${API}/favorites`);
          const fav = favRes.data.find(f => f.target_type === "character" && f.target_id === id);
          setIsFavorite(!!fav);
        } catch { /* ignore */ }
      }
    } catch {
      toast.error("Failed to load character");
    } finally {
      setLoading(false);
    }
  }, [id, user]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleFavorite = async () => {
    if (!user) { toast.error("Please sign in to save favorites"); return; }
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/by-target/character/${character.id}`);
        setIsFavorite(false);
        toast.success("Removed from favorites");
      } else {
        await axios.post(`${API}/favorites`, { target_type: "character", target_id: character.id });
        setIsFavorite(true);
        toast.success("Added to favorites");
      }
    } catch { toast.error("Failed to update favorites"); }
  };

  const handleCommentAdded = (comment) => setComments([comment, ...comments]);
  const handleCommentDeleted = (commentId) => setComments(comments.filter(c => c.id !== commentId));

  const { translated: translatedBio, loading: translatingBio } = useTranslateContent(
    character?.description,
    character?.id ? `char-desc-${character.id}` : ""
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#030712]">
        <Navbar /><div className="spinner"></div>
      </div>
    );
  }

  if (!character) {
    return (
      <div className="min-h-screen flex flex-col bg-[#030712]">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl text-white mb-4">{t("character.notFound")}</h1>
            <Link to="/" className="text-yellow-500 hover:underline">{t("universe.goHome")}</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#030712]">
      <Navbar />
      
      {/* Lightbox */}
      <Lightbox
        images={uniqueImages}
        currentIndex={lightboxIdx}
        onClose={() => setLightboxIdx(-1)}
        onNext={() => setLightboxIdx((lightboxIdx + 1) % uniqueImages.length)}
        onPrev={() => setLightboxIdx((lightboxIdx - 1 + uniqueImages.length) % uniqueImages.length)}
      />

      {/* Hero */}
      <section className="pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {universe && (
            <Link to={`/universe/${universe.slug}`} className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors">
              <ChevronLeft className="w-4 h-4" />
              Back to {universe.name}
            </Link>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Character Image - auto-rotating carousel */}
            <div className="lg:col-span-1">
              <div
                className="card-surface overflow-hidden aspect-[3/4] cursor-pointer group relative"
                onClick={() => uniqueImages.length > 0 && setLightboxIdx(heroIdx)}
                onMouseEnter={() => setHeroPaused(true)}
                onMouseLeave={() => setHeroPaused(false)}
                data-testid="character-main-image"
              >
                {uniqueImages.length > 1 ? (
                  <>
                    {uniqueImages.map((img, i) => (
                      <img
                        key={i}
                        src={img}
                        alt={character.name}
                        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                          i === heroIdx ? "opacity-100" : "opacity-0"
                        }`}
                      />
                    ))}
                  </>
                ) : (
                  <img
                    src={character.image || "https://via.placeholder.com/400x533?text=No+Image"}
                    alt={character.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <ImageIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-70 transition-opacity" />
                </div>
                {/* Dot indicators for carousel */}
                {uniqueImages.length > 1 && (
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                    {uniqueImages.map((_, i) => (
                      <button
                        key={i}
                        onClick={(e) => { e.stopPropagation(); setHeroIdx(i); }}
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                          i === heroIdx ? "w-5 bg-amber-400" : "w-1.5 bg-white/40 hover:bg-white/60"
                        }`}
                        data-testid={`hero-dot-${i}`}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Thumbnail strip */}
              {uniqueImages.length > 1 && (
                <div className="flex gap-2 mt-3 overflow-x-auto pb-1" data-testid="image-thumbnails">
                  {uniqueImages.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => { setHeroIdx(i); setLightboxIdx(i); }}
                      className={`w-16 h-16 rounded-lg overflow-hidden border-2 flex-shrink-0 transition-all hover:opacity-100 ${
                        i === heroIdx ? "border-amber-500/50 opacity-100" : "border-white/10 opacity-60"
                      }`}
                      data-testid={`thumb-${i}`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Character Info */}
            <div className="lg:col-span-2">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    {character.species && (
                      <span className="text-xs font-mono tracking-[0.2em] uppercase text-amber-500 block">{character.species}</span>
                    )}
                    {character.popularity_rank && (
                      <span className="flex items-center gap-1 text-xs font-mono tracking-wider text-amber-400/70 bg-amber-500/10 px-2 py-0.5 rounded-full border border-amber-500/20" data-testid="popularity-rank">
                        <Trophy className="w-3 h-3" /> #{character.popularity_rank}
                      </span>
                    )}
                  </div>
                  <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-white font-['Outfit']">{character.name}</h1>
                  {character.affiliation && <p className="text-slate-400 mt-2">{character.affiliation}</p>}
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  <Button onClick={toggleFavorite} className={`text-xs sm:text-sm ${isFavorite ? "bg-red-500 hover:bg-red-600" : "btn-secondary"}`} data-testid="favorite-button">
                    {isFavorite ? <HeartOff className="w-4 h-4 mr-1 sm:mr-2" /> : <Heart className="w-4 h-4 mr-1 sm:mr-2" />}
                    {isFavorite ? t("character.removeFavorite") : t("character.addToFavorites")}
                  </Button>
                  <ShareButtons title={`${character.name} — FanVerse`} description={character.description} />
                </div>
              </div>

              {/* Quick Info Bar */}
              {(character.first_appearance || character.actors?.length > 0) && (
                <div className="flex flex-wrap gap-3 sm:gap-4 mb-6" data-testid="character-quick-info">
                  {character.first_appearance && (
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-400" data-testid="first-appearance">
                      <Calendar className="w-4 h-4 text-amber-500/70" />
                      <span>{character.first_appearance}</span>
                    </div>
                  )}
                  {character.actors?.length > 0 && (
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-400" data-testid="character-actors">
                      <Film className="w-4 h-4 text-amber-500/70" />
                      <span>{character.actors.join(", ")}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Biography */}
              <div className="card-surface p-8 mb-6">
                <h2 className="text-xl font-bold text-white mb-4 font-['Outfit']">{t("character.biography")}</h2>
                <p className="text-slate-300 leading-relaxed whitespace-pre-line">{translatedBio || character.description}</p>
                {translatingBio && <span className="text-xs text-amber-500/50 mt-2 block">Translating...</span>}
              </div>

              {/* Abilities */}
              {character.abilities?.length > 0 && (
                <div className="card-surface p-8 mb-6">
                  <h2 className="text-xl font-bold text-white mb-4 font-['Outfit']">{t("character.abilities")}</h2>
                  <div className="flex flex-wrap gap-2">
                    {character.abilities.map((ability, index) => (
                      <span key={index} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-yellow-500/10 text-yellow-500 text-sm border border-yellow-500/10">
                        <Zap className="w-3 h-3" /> {ability}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Tools & Weapons */}
              {weapons.length > 0 && (
                <div className="card-surface p-8" data-testid="character-weapons">
                  <h2 className="text-xl font-bold text-white mb-4 font-['Outfit'] flex items-center gap-2">
                    <Swords className="w-5 h-5 text-red-400" /> {t("character.toolsWeapons")}
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {weapons.map((w) => (
                      <div key={w.id} className="flex items-start gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/5 hover:border-red-500/20 transition-all" data-testid={`weapon-${w.id}`}>
                        {w.image ? (
                          <img src={w.image} alt={w.name} className="w-12 h-12 rounded-lg object-cover border border-white/10 flex-shrink-0" />
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                            <Swords className="w-5 h-5 text-red-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <h3 className="text-white text-sm font-semibold font-['Outfit'] truncate">{w.name}</h3>
                            <span className="text-[9px] font-mono uppercase tracking-wider text-slate-500 capitalize flex-shrink-0">{w.type?.replace("_", " ")}</span>
                          </div>
                          <p className="text-slate-500 text-xs line-clamp-2">{w.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Image Gallery (multi-image with lightbox) */}
      {uniqueImages.length > 1 && (
        <section className="py-12 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-6 font-['Outfit'] flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-purple-400" /> {t("character.gallery")}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="character-image-gallery">
              {uniqueImages.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setLightboxIdx(i)}
                  className="aspect-square rounded-xl overflow-hidden border border-white/5 hover:border-purple-500/30 transition-all group cursor-pointer"
                  data-testid={`gallery-image-${i}`}
                >
                  <img src={img} alt={`${character.name} ${i + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* External Gallery */}
      {gallery.length > 0 && (
        <section className="py-12 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-6 font-['Outfit']">Community Gallery</h2>
            <GalleryGrid items={gallery} />
          </div>
        </section>
      )}

      {/* Comments */}
      <section className="py-12 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <CommentSection
            targetType="character" targetId={character.id}
            comments={comments} onCommentAdded={handleCommentAdded} onCommentDeleted={handleCommentDeleted}
          />
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CharacterPage;
